import store from '../../redux/store'

// The pocket mark: the word's mastery underline, with a short tab dropping from its end. Words the reader
// already underlines (new, learning) only gain the tab; the others get the line in their mastery colour
// too. Copies that carry a mastery class take its colour (wordPocket.css); this is the fallback.
const MASTERY_COLOR: Record<string, string> = {
  New: '#068466',
  Learning: '#FC9F5B',
  Growing: '#7C8B9C',
  Developing: '#4A5F78',
  Mastered: '#001E42',
}
const DEFAULT_COLOR = MASTERY_COLOR.New
const NATIVE_LINE = '.newword-highlight, .learningword-highlight, .oldword-highlight'
const STYLE_ID = 'word-pocket-rules'

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

export function hashString(s: string) {
  let h = 2166136261
  for (const c of Array.from(s)) {
    h ^= c.codePointAt(0) || 0
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

const cssString = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
const selectorFor = (word: string) => `[data-w="${cssString(word)}"]`
const colorFor = (word: string) => {
  const mastery = store.getState().story?.storyDictionary?.[word]?.mastery
  return (typeof mastery === 'string' && MASTERY_COLOR[mastery]) || DEFAULT_COLOR
}

// Words still animating their exit, kept marked until the tab has gone.
const leaving = new Set<string>()
let lastKey = ''

/** Writes the rules that mark every copy of every pocketed word, on every page, with no re-render. */
export function syncPocketRules() {
  const words = Array.from(
    new Set(
      store
        .getState()
        .pocket.words.map((w) => w.word)
        .concat(Array.from(leaving)),
    ),
  )
  const colors = words.map(colorFor)
  const key = words.map((w, i) => `${w}:${colors[i]}`).join('|')
  if (key === lastKey) return
  lastKey = key
  let el = document.getElementById(STYLE_ID)
  if (!el) {
    el = document.createElement('style')
    el.id = STYLE_ID
    document.head.appendChild(el)
  }
  if (!words.length) {
    el.textContent = ''
    return
  }
  const list = (suffix: string, prefix = '') => words.map((w) => `${prefix}${selectorFor(w)}${suffix}`).join(',')
  el.textContent = [
    // The mark draws the line itself, so it can animate; the reader's own border steps aside (the doubled
    // class outranks the mastery classes' !important borders, hover included).
    `${list('', '.main_title_big_text.main_title_big_text')}{border-bottom-color:transparent!important}`,
    `${list('')}{position:relative}`,
    `${list('::before')}{content:"";position:absolute;left:0;right:0;bottom:-2px;height:2px;background:var(--pk-c);pointer-events:none}`,
    `${list('::after')}{content:"";position:absolute;right:0;bottom:calc(-2px - var(--pk-tab-h,clamp(3px,.16em,5px)));width:var(--pk-tab-w,clamp(7px,.36em,11px));height:var(--pk-tab-h,clamp(3px,.16em,5px));background:var(--pk-c);-webkit-mask:var(--pk-tab-mask) 0 0/100% 100% no-repeat;mask:var(--pk-tab-mask) 0 0/100% 100% no-repeat;transform-origin:50% 0;pointer-events:none}`,
    ...words.map((w, i) => `${selectorFor(w)}{--pk-c:${colors[i]}}`),
  ].join('\n')
}

const visibleCopies = (word: string) =>
  Array.from(document.querySelectorAll<HTMLElement>(selectorFor(word))).filter((el) => el.getClientRects().length > 0)

const center = (el: Element) => {
  const r = el.getBoundingClientRect()
  return [r.left + r.width / 2, r.top + r.height / 2]
}

const ENTER = ['pk-in', 'pk-in-line', 'pk-out', 'pk-out-line', 'pk-fade']
const clearAnim = (el: HTMLElement) => el.classList.remove(...ENTER)

/**
 * Marks every visible copy, rippling out from the copy the learner tapped: a word with no underline
 * draws its line first, then the tab drops from it.
 */
export function drawUnderlines(word: string, source?: Element | null) {
  syncPocketRules()
  const copies = visibleCopies(word)
  if (prefersReducedMotion()) {
    copies.forEach((el) => {
      clearAnim(el)
      el.classList.add('pk-fade')
      setTimeout(() => el.classList.remove('pk-fade'), 400)
    })
    return
  }
  const [sx, sy] = source && source.getClientRects().length ? center(source) : [0, 0]
  copies
    .map((el) => {
      const [x, y] = center(el)
      return { el, d: Math.hypot(x - sx, y - sy) }
    })
    .sort((a, b) => a.d - b.d)
    .forEach(({ el }, i) => {
      const delay = Math.min(i * 45, 500)
      const drawLine = !el.matches(NATIVE_LINE)
      el.style.setProperty('--pkd', `${delay}ms`)
      el.style.setProperty('--pkt', `${delay + (drawLine ? 200 : 60)}ms`)
      clearAnim(el)
      // Restart the animation even when the same classes go straight back on.
      void el.offsetWidth
      el.classList.add('pk-in')
      if (drawLine) el.classList.add('pk-in-line')
      setTimeout(() => clearAnim(el), delay + 700)
    })
}

/** Folds the tab back up, wipes away a line the reader would not draw, then drops the word's rules. */
export function undrawUnderlines(word: string): Promise<void> {
  leaving.add(word)
  syncPocketRules()
  const copies = visibleCopies(word)
  const reduced = prefersReducedMotion()
  copies.forEach((el, i) => {
    el.style.setProperty('--pkd', reduced ? '0ms' : `${Math.min(i * 25, 250)}ms`)
    clearAnim(el)
    el.classList.add('pk-out')
    if (!el.matches(NATIVE_LINE)) el.classList.add('pk-out-line')
  })
  return new Promise((resolve) =>
    setTimeout(
      () => {
        leaving.delete(word)
        syncPocketRules()
        copies.forEach(clearAnim)
        resolve()
      },
      reduced ? 200 : Math.min(copies.length * 25, 250) + 420,
    ),
  )
}
